import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useUIStore } from '../stores/ui.js';

/**
 * Registra atalhos globais de teclado AXIOMA.
 *
 * Leader 'g' seguido de:
 *   a → /agenda
 *   k → /kanban
 *   c → /carteira
 *   r → /risco
 *   p → /processos
 *
 * Standalone:
 *   ?       → ajuda
 *   ⌘/Ctrl+K → command palette
 *   ⌘/Ctrl+B → toggle sidebar
 */
export function useShortcuts(): void {
  const navigate = useNavigate();
  const ui = useUIStore();
  const leaderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inLeaderModeRef = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const isInputting =
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable);
      if (isInputting && e.key !== 'Escape') {
        return;
      }

      // Cmd/Ctrl + K → command palette
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ui.setCommandPaletteOpen(true);
        return;
      }
      // Cmd/Ctrl + B → toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        ui.toggleSidebar();
        return;
      }
      if (e.key === '?') {
        e.preventDefault();
        ui.setHelpOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        ui.setCommandPaletteOpen(false);
        ui.setHelpOpen(false);
        return;
      }

      // Leader 'g'
      if (inLeaderModeRef.current) {
        const map: Record<string, string> = {
          a: '/agenda',
          k: '/kanban',
          c: '/carteira',
          r: '/risco',
          p: '/processos',
        };
        const target = map[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        inLeaderModeRef.current = false;
        if (leaderTimerRef.current) {
          clearTimeout(leaderTimerRef.current);
        }
        return;
      }

      if (e.key.toLowerCase() === 'g') {
        inLeaderModeRef.current = true;
        if (leaderTimerRef.current) {
          clearTimeout(leaderTimerRef.current);
        }
        leaderTimerRef.current = setTimeout(() => {
          inLeaderModeRef.current = false;
        }, 1500);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, ui]);
}
